import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    ArrowLeft, RefreshCcw, FileWarning, Search, AlertCircle,
    CheckCircle2, Clock, ShieldAlert, Globe,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
    created: { label: 'Créé', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    deduplicated: { label: 'Dédupliqué', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: RefreshCcw },
    refused_api_key: { label: 'Refusé (clé API)', cls: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert },
    refused_validation: { label: 'Refusé (champs invalides)', cls: 'bg-orange-100 text-orange-700 border-orange-200', icon: FileWarning },
    refused_rate_limit: { label: 'Refusé (rate-limit)', cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: Clock },
};

export default function PublicFormLogs() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (search.trim()) params.search = search.trim();
            const [r, s] = await Promise.all([
                axios.get(`${API_URL}/api/admin/public-form-logs`, { params, withCredentials: true }),
                axios.get(`${API_URL}/api/admin/public-form-logs/stats`, { withCredentials: true }),
            ]);
            setRows(r.data || []);
            setStats(s.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const formatTime = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const renderPayload = (p) => {
        if (!p || typeof p !== 'object') return '—';
        const fields = [];
        if (p.nom) fields.push(<span key="n"><strong>{p.nom}</strong></span>);
        if (p.telephone) fields.push(<span key="t" className="text-gray-600">📞 {p.telephone}</span>);
        if (p.email) fields.push(<span key="e" className="text-gray-600">✉ {p.email}</span>);
        if (p.adresse) fields.push(<span key="a" className="text-gray-500 text-xs">de {p.adresse}</span>);
        if (p.adresse_arrivee) fields.push(<span key="aa" className="text-gray-500 text-xs">vers {p.adresse_arrivee}</span>);
        if (p.message) fields.push(<span key="m" className="text-gray-500 text-xs italic">"{p.message}"</span>);
        return fields.length ? (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">{fields}</div>
        ) : <span className="text-gray-400 italic">payload vide</span>;
    };

    const total = stats?.total || 0;
    const counts = stats?.by_status || {};

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/administration')} data-testid="back-btn">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <Globe className="w-6 h-6 text-[#1B365D]" />
                                Logs formulaire site
                            </h1>
                            <p className="text-sm text-gray-500">
                                Toutes les soumissions reçues depuis luxymove-demenagement.fr (90 derniers jours)
                            </p>
                        </div>
                    </div>
                    <Button onClick={fetchAll} variant="outline" size="sm" data-testid="refresh-btn">
                        <RefreshCcw className="w-4 h-4 mr-1" /> Actualiser
                    </Button>
                </div>

                {/* Stats cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Card className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">7 derniers jours</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{total}</p>
                    </Card>
                    {Object.entries(STATUS_LABELS).map(([k, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                            <Card key={k} className="p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                    <Icon className="w-3 h-3" /> {cfg.label}
                                </p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{counts[k] || 0}</p>
                            </Card>
                        );
                    })}
                </div>

                {/* Diagnostic banner */}
                {(counts.refused_api_key > 0 || counts.refused_validation > 0 || counts.refused_rate_limit > 0) && (
                    <Card className="p-4 bg-amber-50 border-amber-200">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-semibold text-amber-900 mb-1">
                                    {(counts.refused_api_key || 0) + (counts.refused_validation || 0) + (counts.refused_rate_limit || 0)} soumission(s)
                                    refusée(s) ces 7 derniers jours
                                </p>
                                <ul className="text-amber-800 space-y-0.5 list-disc list-inside">
                                    {counts.refused_api_key > 0 && (
                                        <li>{counts.refused_api_key} refusée(s) pour clé API invalide — vérifiez que votre site envoie bien le bon header X-API-Key</li>
                                    )}
                                    {counts.refused_validation > 0 && (
                                        <li>{counts.refused_validation} refusée(s) pour champs invalides — voir détail ci-dessous</li>
                                    )}
                                    {counts.refused_rate_limit > 0 && (
                                        <li>{counts.refused_rate_limit} refusée(s) pour rate-limit (10 envois/min/IP dépassés)</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Filters */}
                <div className="flex gap-2 flex-wrap items-center">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') fetchAll(); }}
                            placeholder="Rechercher nom, téléphone, email, IP…"
                            className="pl-9"
                            data-testid="search-input"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[220px]" data-testid="status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            <SelectItem value="created">Créés</SelectItem>
                            <SelectItem value="deduplicated">Dédupliqués</SelectItem>
                            <SelectItem value="refused_api_key">Refusés (clé API)</SelectItem>
                            <SelectItem value="refused_validation">Refusés (champs invalides)</SelectItem>
                            <SelectItem value="refused_rate_limit">Refusés (rate-limit)</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button onClick={fetchAll} size="sm" variant="outline">Filtrer</Button>
                </div>

                {/* Table */}
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <th className="px-4 py-3">Reçu le</th>
                                    <th className="px-4 py-3">Statut</th>
                                    <th className="px-4 py-3">Données soumises</th>
                                    <th className="px-4 py-3">IP</th>
                                    <th className="px-4 py-3">Raison / Détail</th>
                                    <th className="px-4 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading && (
                                    <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">Chargement…</td></tr>
                                )}
                                {!loading && rows.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">Aucune soumission trouvée</td></tr>
                                )}
                                {!loading && rows.map((r) => {
                                    const cfg = STATUS_LABELS[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-700' };
                                    return (
                                        <tr key={r.id} className="hover:bg-gray-50" data-testid={`log-row-${r.id}`}>
                                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatTime(r.received_at)}</td>
                                            <td className="px-4 py-3">
                                                <Badge className={`${cfg.cls} border`}>{cfg.label}</Badge>
                                            </td>
                                            <td className="px-4 py-3">{renderPayload(r.payload)}</td>
                                            <td className="px-4 py-3 text-gray-500 text-xs font-mono">{r.ip}</td>
                                            <td className="px-4 py-3 text-gray-600 text-xs max-w-md">{r.reason || '—'}</td>
                                            <td className="px-4 py-3">
                                                {r.prospect_id && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => navigate(`/prospects/${r.prospect_id}`)}
                                                        data-testid={`view-prospect-${r.id}`}
                                                    >
                                                        Voir prospect
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </Layout>
    );
}
